/**
 * Represents one Patient resource (DomainResource)
 * 
 */

import HumanName from '../Core/HumanName';
import { ContactPoint } from '../Core/ContactPoint';
import { DomainResource } from '../DomainResource/DomainResource';


class Patient extends DomainResource {
  
  /**
   * Patient
   * 
   * @param string resourceId
   * @param string identifierCode Optional personal code 
   * @return void
   */
  constructor(
    resourceId,
    identifierCode : string
  ) {
    super( 'Patient' );

    this._id = resourceId;
    this.anonyousName = null;
    this.birthDate = null;

    if( identifierCode ){
      this.identifierCode = identifierCode;
    }

    this._telecoms = [];
    this._communication = [];
  }


  /**
   * 
   * @param code string
   * @param code systemName
   * @return string
   */
  replaceIdentifierCode( code, systemName ) {
    this.identifierCode = code;


    let system = "http://documental.eu/fhir/naming/person/personal_id";

    if( typeof systemName !== "undefined" && systemName !== null && systemName ){
      system = systemName;
    }

    if( this._resourceSetByLiteral ){
      let foundIndex = -1;

      for( var ii in this._resourceLiteral.identifier ){
        if( 
          typeof this._resourceLiteral.identifier[ ii ].system !== "undefined" && 
          this._resourceLiteral.identifier[ ii ].system === system
         ){
          foundIndex = ii;
        }
      }

      if( foundIndex > -1 ){ 
        this._resourceLiteral.identifier[ foundIndex ].value = code;
      } else {
        this._resourceLiteral.identifier.push( {
          	"system" : system,
            "value" : code
        } );
      }
    }
  }


  /**
   * 
   * 
   * @param string gender - male | female | other | unknown
   * @return void
  setGender( gender : string ) {
    this.gender = gender;
  }
  */

  /**
   * 
   * 
   * @param HumanName name
   * @return void
   */
  setOfficialName( name : HumanName ) {
    this.officialName = name;
    
    if( this._resourceSetByLiteral ){
      
      let prevKey = -1;
      if( typeof this._resourceLiteral.name !== "undefined" ){
        for( var i in this._resourceLiteral.name ){
          if( this._resourceLiteral.name[ i ].use === name.getNameUseType() ){
            prevKey = i;
          }
        }
      } else {
        this._resourceLiteral.name = [];
      }

      if( prevKey < 0 ){
        prevKey = this._resourceLiteral.name.length;
      }
      
      this._resourceLiteral.name[ prevKey ] = {
        "family": name.getLastName(),
        "given": name.getGivenNames(),
        "use" : name.getNameUseType() 
      };
    }
  }


  /**
   * Adds telecom or replaces former one by system code like phone or e-mail
   * 
   * @param ContactPoint
   * @return void
   */
  updateTelecomBySystemCode( cp : ContactPoint ) {
    let foundIndex = -1;
    for( var i in this._telecoms ){
      if( this._telecoms[ i ].getSystemCode() == cp.getSystemCode() ){
        foundIndex = i;
      }
    }

    if( foundIndex < 0 ){
      foundIndex = this._telecoms.length;
    }

    this._telecoms[ foundIndex ] = cp;
    
    // overwrite telecom-s in resource literal
    if( this._resourceSetByLiteral ){
      this._resourceLiteral.telecom = [];

      for( var i in this._telecoms ){
        this._resourceLiteral.telecom.push( this._telecoms[ i ].toObject() );
      }
    }
  }


  /**
   * Adds telecom or replaces former one by system code like phone or e-mail
   * 
   * @param code
   * @return void
   */
  removeTelecomsBySystemCode( code ) {
    for( var i in this._telecoms ){
      if( this._telecoms[ i ].getSystemCode() == code ){
        delete this._telecoms[ i ];
      }
    }

    // overwrite telecom-s in resource literal
    if( this._resourceSetByLiteral ){
      this._resourceLiteral.telecom = [];

      for( var i in this._telecoms ){
        this._resourceLiteral.telecom.push( this._telecoms[ i ].toObject() );
      }
    }
  }


  /**
   * get telecom by system code like phone or email
   * 
   * @param code
   * @return void
   */
  getFirstTelecomBySystemCode( code ) {
    for( var i in this._telecoms ){
      if( this._telecoms[ i ].getSystemCode() == code ){
        return this._telecoms[ i ];
      }
    }

    return null;
  }

  
  /**
   * 
   * 
   * @param string birthday in Format YYYY-mm-dd eg: 1978-12-09
   * @return void
  */
  setBirthDay( birthday ) {
    this.birthDate = birthday;

    if( this._resourceSetByLiteral ){
      this._resourceLiteral.birthDate = birthday;
    }
  }

  /**
   * Return birthday as string in format of YYYY-mm-dd eg: 1978-12-09
   * 
   * @return string
  */
  getBirthDay() {
    return this.birthDate;
  }
  

  /**
   * Adds or updates communication language
   * If "setAsPreferred" is set true, all other languages will be changed to un preferred
   * 
   * @param string languagecode
   * @param string languageDisplay
   * @param boolean setAsPreferred
   * @return void
   */
  addCommunicationLanguage( languagecode, languageDisplay, setAsPreferred ) {
    let foundIndex = -1;

    for( var i in this._communication ){
      if( this._communication[ i ].code == languagecode ){
        foundIndex = i;
      }
      if( setAsPreferred ){
        this._communication[ i ].preferred = false;
      }
    }

    if( foundIndex < 0 ){
      foundIndex = this._communication.length;

      this._communication[ foundIndex ] = {
        "objType" : "humanLanguage",
        "code" : languagecode,
        "system" : "urn:ietf:bcp:47",
        "preferred" : setAsPreferred
      };

      if( languageDisplay !== null && languageDisplay !== "" ){
        this._communication[ foundIndex ].display = languageDisplay;
      }
    }

    if( setAsPreferred ){
      this._communication[ foundIndex ].preferred = true;
    }

    // overwrite telecom-s in resource literal
    if( this._resourceSetByLiteral ){
      this._resourceLiteral.communication = [];

      for( var i in this._communication ){
        this._resourceLiteral.communication.push( this._buildCommunicationFhirJson( this._communication[ i ] ) );
      }
    }
  }


  /**
   * 
   * @return string
   */
  getIdentifierCodeAsString() {
    return this.identifierCode;
  }


  /**
   * -
   * 
   * @return string
   */
  getFirstAddressAsString(){
    if( typeof  this._resourceLiteral.address != 'undefined' ){
      for( var i in this._resourceLiteral.address ){
          return this._resourceLiteral.address[ i ].text;
      }
    }

    return '';
  }

  /**
   * 
   * @return string
   */
  getOfficialName() {
    if( typeof this.officialName == 'undefined' ){
      return null;
    }
    return this.officialName;
  }

  /**
   * 
   * @return string
   */
  getAnonymousName() {
    if( typeof this.anonyousName == 'undefined' || this.anonyousName == null ){
      return '';
    }
    return this.anonyousName;
  }

  /**
   * 
   * @return string
   */
  getFullName() {
    if( typeof this.officialName == 'undefined' || this.officialName == null ){
      return '';
    }
    return this.officialName.getFullDisplayName();
  }


  /**
   * 
   * 
   * @return string|null
   *  
   **/
  getFirstCommunicationLanguageCode() {
    for( var ii in this._communication ){   
      return this._communication[ ii ].code;
    }

    return null;
  }

  
  /**
   * Extract simpler object from FHIR communication chema like this: 
   * Used system : http://terminology.hl7.org/3.1.0/CodeSystem-v3-ietf3066.html  / urn:ietf:bcp:47
   * 
   * {
      "language": {
        "coding": [
          {
            "system": "urn:ietf:bcp:47",
            "code": "nl",
            "display": "Dutch"
          }
        ],
        "text": "Nederlands"
      },
      "preferred": true
    }
   * 
   */
  _extractCommunicationFromJson( fhirJson ){

    if( 
      typeof fhirJson.language === "undefined" || 
      typeof fhirJson.language.coding === "undefined" || 
      typeof fhirJson.language.coding[ 0 ] === "undefined" || 
      typeof fhirJson.language.coding[ 0 ].code === "undefined"
    ){
      return null;
    }

    let communicationObj = {
      "objType" : "humanLanguage",
      "code" : fhirJson.language.coding[ 0 ].code,
      "system" : "urn:ietf:bcp:47",
      "display" : "",
      "languageText" : "",
      "preferred" : false
    }
    
    if( typeof fhirJson.language.coding[ 0 ].system !== "undefined" ){
      communicationObj.system = fhirJson.language.coding[ 0 ].system;
    }
    
    if( typeof fhirJson.language.coding[ 0 ].display !== "undefined" ){
      communicationObj.display = fhirJson.language.coding[ 0 ].display;
    }
    
    if( typeof fhirJson.language.text !== "undefined" ){
      communicationObj.languageText = fhirJson.language.text;
    }

    if( typeof fhirJson.preferred !== "undefined" ){
      communicationObj.preferred = fhirJson.preferred;
    }

    return communicationObj;
  }

  /**
   * From simpler inetrnal object to FHIR one
   * 
   * @returns 
   */
  _buildCommunicationFhirJson( communicationObj ){
    let codeObj = {
      "code": communicationObj.code
    };
    
    if( typeof communicationObj.system !== "undefined" ){
      codeObj.system = communicationObj.system;
    }
    
    if( typeof communicationObj.display !== "undefined" ){
      codeObj.display = communicationObj.display;
    }

    let fhirJson = {
      "language": {
        "coding": [
          codeObj
        ]
      },
      "preferred": communicationObj.preferred
    }

    if( typeof communicationObj.languageText !== "undefined" ){
      fhirJson.language.text = communicationObj.languageText;
    }

    return fhirJson;
  }
  

  /**
   * Overwrite parent method 
   * 
   * Set local variables based on input JSON
   * 
   * @param object json
   * @return boolean
   */
  setRawResourceObject( json ) {
    super.setRawResourceObject( json );
    
    if( typeof json.name != 'undefined' ){
      for( var i in json.name ){
        if( 
          typeof json.name[ i ].family != 'undefined' && 
          typeof json.name[ i ].given != 'undefined'
        ){
          let firstnames = '';
          //let nameType = json.name[ i ].use;

          for( var j in json.name[ i ].given ){
            firstnames = firstnames + ( firstnames === '' ? '' : ' ' ) + json.name[ i ].given[ j ];
          }

          firstnames = firstnames.trim();

          //if( nameType === 'official' ){
            let fullName = new HumanName(
              'official', firstnames, json.name[ i ].family
            );
            this.officialName = fullName;
          //}

        }


        if( 
          typeof json.name[ i ].use !== 'undefined' && 
          typeof json.name[ i ].text !== 'undefined' && 
          json.name[ i ].use === 'anonymous' 
        ){
          this.anonyousName = json.name[ i ].text;
        }


      }
    }
    
    if( typeof json.birthDate !== 'undefined' ){
      this.birthDate = json.birthDate;
    }
    
    if( typeof json.identifier != 'undefined' ){
      for( var i in json.identifier ){
        if( 
          typeof json.identifier[ i ].system != 'undefined' && 
          typeof json.identifier[ i ].value != 'undefined' && 
          json.identifier[ i ].system == 'http://documental.eu/fhir/naming/person/personal_id'
        ){
          this.identifierCode = json.identifier[ i ].value;
        }
      }
    }

    if( typeof json.telecom != 'undefined' ){
      for( var i in json.telecom ){
        if( 
          typeof json.telecom[ i ].system != 'undefined' && 
          typeof json.telecom[ i ].value != 'undefined'
        ){
          let telecom = new ContactPoint(
            json.telecom[ i ].value, json.telecom[ i ].system, null
          );
          this._telecoms.push( telecom );
        }
      }
    }

    if( typeof json.communication != 'undefined' ){
      for( var i in json.communication ){
        let communicationObj = this._extractCommunicationFromJson( json.communication[ i ] );
        if( communicationObj !== null  ){
          this._communication.push( communicationObj );
        }
      }
    }

  }


  /**
   * Overwrite parent method 
   * 
   * Build basic resource literal
   * This needs to be overwritten by actual resource
   * 
   * @return object
   */
  buidResourceLiteral() {
    super.buidResourceLiteral();

    //Dont build- it's already as json
    if( this._resourceSetByLiteral ){
      return;
    }
    
    this._resourceLiteral.identifier = [
      {
        "system": "http://documental.eu/fhir/naming/person/personal_id",
        " value": this.getIdentifierCodeAsString()
      }
    ];

    var fullName = this.getOfficialName();
    if( fullName != null ){
      this._resourceLiteral.name = [
        {
          "family": fullName.getLastName(),
          "given": fullName.getGivenNames()
        }
      ];
    }

    if( typeof this.birthDate !== null ){
      this._resourceLiteral.birthDate = this.birthDate;
    }
    
    if( typeof this._resourceLiteral.telecom == 'undefined' ){
      this._resourceLiteral.telecom = [];
    }
    
    for( var i in this._telecoms ){
      this._resourceLiteral.telecom.push( this._telecoms.toObject() );
    }

    for( var i in this._communication ){
      this._resourceLiteral.communication.push( this._buildCommunicationFhirJson( this._communication[ i ] ) );
    }
    
  }

}


export default Patient;